import React, { Component } from "react";
import styled, { css } from "styled-components";
import { Button } from "@vitalsource/vstui";
import { cloud } from "@vitalsource/vstui/colors";
import * as PropTypes from "prop-types";
import { cardContentPropTypes } from "../../types";
import FigureOrImage from "../../../../../../apps/overleaf/components/figure-or-image";
import { TYPE_IMAGE } from "../../../../../../stores/overleaf-store";
import TruncateMarkup from "react-truncate-markup";
import { VisibleLineBreaks } from "../../../../../../ui/components/mixins";
import { debounce } from "throttle-debounce";

const MAIN_PADDING = 24;

const Styled = {
  CardContainer: styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    text-align: center;
    padding: ${MAIN_PADDING}px;
    color: ${cloud.getShade(10)};

    // https://github.com/vitalsource/vstui/issues/238#issuecomment-508168646
    button {
      visibility: inherit;
    }
  `,
  ShortTextContainer: styled.div`
    font-size: 1.7rem;
    font-weight: 500;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    padding: 24px;
  `,
  LongTextContainer: styled.div`
    font-size: 1.5rem;
    font-weight: 300;
    width: 100%;
  `,
  ExpandButtonContainer: styled.div`
    position: absolute;
    right: 3px;
    bottom: 4px;
  `,
  FigureStyles: css`
    position: relative;
    width: 100%;
    height: 100%;
  `,
  ImageStyles: css`
    max-height: 127px;
    max-width: 250px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: block;
    border: 1px solid ${cloud.getShade(4)};
  `
};

export default class CardContent extends Component {
  state = {
    expanded: false,
    truncated: false
  };

  componentWillUnmount() {
    window.removeEventListener("resize", this.onResize);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.expanded !== prevState.expanded) {
      this.sendHeight();
    }
    if (this.state.expanded && !prevState.expanded) {
      window.addEventListener("resize", this.onResize);
    } else if (!this.state.expanded && prevState.expanded) {
      window.removeEventListener("resize", this.onResize);
    }
  }

  onResize = debounce(100, () => {
    this.sendHeight();
  });

  sendHeight() {
    const h = this.state.expanded
      ? this.container.clientHeight + MAIN_PADDING * 2
      : undefined;
    this.props.onHeightChange(h);
  }

  onToggleExpand = e => {
    e.stopPropagation();
    this.setState(prevState => ({
      expanded: !prevState.expanded
    }));
  };

  onTruncated = truncated => {
    if (!this.state.truncated) {
      this.setState({
        truncated
      });
    }
    return "…";
  };

  render() {
    let { cardContent, shortText } = this.props;
    let { expanded } = this.state;

    if (cardContent.type === TYPE_IMAGE) {
      return (
        <FigureOrImage
          src={cardContent.content}
          title={cardContent.metadata.title}
          caption={cardContent.metadata.caption}
          alt={cardContent.metadata.alt}
          figureStyles={Styled.FigureStyles}
          imageStyles={Styled.ImageStyles}
        />
      );
    } else if (shortText) {
      // Use character counts for rendering performance reasons
      // No line breaks means this is relatively safe
      let contentToShow = cardContent.content;
      let title = undefined;
      if (contentToShow.length > 200) {
        title = cardContent.content;
        contentToShow = contentToShow.substr(0, 135) + "...";
      }

      return (
        <Styled.CardContainer>
          <Styled.ShortTextContainer title={title}>
            {contentToShow}
          </Styled.ShortTextContainer>
        </Styled.CardContainer>
      );
    } else {
      return (
        <Styled.CardContainer>
          <Styled.LongTextContainer innerRef={el => (this.container = el)}>
            {this.state.expanded ? (
              <VisibleLineBreaks>{cardContent.content}</VisibleLineBreaks>
            ) : (
              <TruncateMarkup ellipsis={this.onTruncated} lines={6}>
                <span>
                  {cardContent.content.split("\n").map((el, i) => (
                    <div key={i}>{el}</div>
                  ))}
                </span>
              </TruncateMarkup>
            )}
          </Styled.LongTextContainer>
          {this.state.truncated && (
            <Styled.ExpandButtonContainer>
              <Button
                onClick={this.onToggleExpand}
                aria-expanded={expanded}
                naked
                type="button"
              >
                {i18n.t(`reader.flashcards.${expanded ? "less" : "more"}`)}
              </Button>
            </Styled.ExpandButtonContainer>
          )}
        </Styled.CardContainer>
      );
    }
  }
}

CardContent.propTypes = {
  cardContent: cardContentPropTypes.isRequired,
  shortText: PropTypes.bool
};
